import React from 'react'
import { Link } from 'react-router-dom'
import { PenToolIcon, SparklesIcon } from 'lucide-react'
// Creating custom icons since they don't exist in lucide-react
const ConnectIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
  </svg>
)
const SparklesIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"></path>
  </svg>
)
const PenToolIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="m12 19 7-7 3 3-7 7-3-3z"></path>
    <path d="m18 13-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
    <path d="m2 2 7.586 7.586"></path>
    <circle cx="11" cy="11" r="2"></circle>
  </svg>
)
const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold font-display text-charcoal">
            How ChefChat Works
          </h2>
          <p className="mt-4 text-lg text-sage max-w-2xl mx-auto">
            Get up and running in minutes, not weeks
          </p>
        </div>
        <div className="relative">
          {/* Connection line */}
          <div className="hidden md:block absolute top-24 left-0 right-0 h-0.5 bg-burgundy/20 z-0"></div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-cream rounded-lg p-6 shadow-md relative z-10">
              <div className="w-12 h-12 bg-burgundy rounded-full flex items-center justify-center mb-5 mx-auto">
                <ConnectIcon className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-charcoal mb-3 text-center">
                1. Connect Your Accounts
              </h3>
              <p className="text-sage text-center">
                Connect ChefChat to your existing communication channels -
                social media, website chat, email, and more.
              </p>
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-sm text-center text-sage">
                  <span className="font-semibold text-burgundy block mb-1">
                    Takes just 2 minutes
                  </span>
                  Secure, OAuth-based connections with all major platforms
                </p>
              </div>
            </div>
            <div className="bg-cream rounded-lg p-6 shadow-md relative z-10">
              <div className="w-12 h-12 bg-burgundy rounded-full flex items-center justify-center mb-5 mx-auto">
                <PenToolIcon className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-charcoal mb-3 text-center">
                2. Train Your AI Voice
              </h3>
              <p className="text-sage text-center">
                Answer a few questions about your restaurant and provide sample
                responses to train ChefChat in your unique voice.
              </p>
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-sm text-center text-sage">
                  <span className="font-semibold text-burgundy block mb-1">
                    Takes just 3 minutes
                  </span>
                  Our AI learns your communication style and restaurant details
                </p>
              </div>
            </div>
            <div className="bg-cream rounded-lg p-6 shadow-md relative z-10">
              <div className="w-12 h-12 bg-burgundy rounded-full flex items-center justify-center mb-5 mx-auto">
                <SparklesIcon className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-charcoal mb-3 text-center">
                3. Watch the Magic Happen
              </h3>
              <p className="text-sage text-center">
                ChefChat begins responding to customer inquiries instantly,
                learning and improving with every interaction.
              </p>
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-sm text-center text-sage">
                  <span className="font-semibold text-burgundy block mb-1">
                    Immediate Results
                  </span>
                  You maintain full oversight and can adjust settings anytime
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-16 bg-burgundy/5 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-semibold text-charcoal">
            Ready to reclaim your time?
          </h3>
          <p className="mt-4 text-sage max-w-2xl mx-auto">
            Join hundreds of restaurant owners who are focusing on their food
            and guests, not their inbox
          </p>
          <div className="mt-6">
            <Link
              to="/signup"
              className="inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-burgundy hover:bg-burgundy/90 transition-colors"
            >
              Start Your Free Trial
            </Link>
            <p className="mt-3 text-sm text-sage">
              No credit card required • Set up in 5 minutes
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
export default HowItWorks
